import * as Sentry from "@sentry/react";

const EVAL_REGEX = /^(\d+(?:\.\d+)?)x(\d+)$/i;
const UDM_THEN_REGEX = /(\d+(?:\.\d+)?)\s*UDM\s*,?\s*then\s+(\d+(?:\.\d+)?)/i;
const UDM_ONLY_REGEX = /(\d+(?:\.\d+)?)\s*UDM/i;
const NUMBER_REGEX = /(\d+(?:\.\d+)?)/;

/** Allowed fee formats
   150
   $150TBD
   120x7; $90
   80x5; 15TBD
   655x1; 455 UDM, then 80
   452 UDM, then 22
 */
export const FEE_VALUE_FORMAT_REGEX = /^[A-Za-z0-9x;$., ]*$/;

export type FeeCalculation = {
    evalFee: string;
    evalSessions: string;
    udmFee: string;
    ongoingFee: string;
    isTbd: boolean;
};

// Supported fee formats:
// 1. 80x5; $15 TBD (eval + TBD ongoing)
// 2. 120TBD (TBD only)
// 3. 25 (single fee)
// 4. 120x7; $90 (eval + ongoing)
// 5. 455x1; 255 (eval + ongoing)
// 6. 655x1; 455 UDM, then 80 (eval + UDM + ongoing)
// 7. 165.76x2; 160.89 UDM, then 32.18 (eval + UDM + ongoing)
// 8. 452 UDM, then 22 (UDM + ongoing)
export function calculateFees(fee: string): FeeCalculation | null {
    const rawFee = `${fee}`.trim();
    if (!rawFee) {
        return null;
    }

    try {
        const normalized = rawFee
            .replace(/\$/g, "")
            .replace(/\([^)]*\)/g, "")
            .trim();

        const isTbd = /TBD/i.test(normalized);
        const withoutTbd = normalized.replace(/TBD/i, "").trim();

        let evalFee = "";
        let evalSessions = "";
        let udmFee = "";
        let ongoingFee = "";
        let remainderPart = "";

        const semicolonIndex = withoutTbd.indexOf(";");

        if (semicolonIndex !== -1) {
            const before = withoutTbd.substring(0, semicolonIndex).trim();
            const after = withoutTbd.substring(semicolonIndex + 1).trim();
            const evalMatch = before.match(EVAL_REGEX);
            if (evalMatch) {
                [, evalFee, evalSessions] = evalMatch;
                remainderPart = after;
            } else {
                remainderPart = withoutTbd;
            }
        } else {
            const evalMatch = withoutTbd.match(EVAL_REGEX);
            if (evalMatch) {
                [, evalFee, evalSessions] = evalMatch;
            } else {
                remainderPart = withoutTbd;
            }
        }

        if (remainderPart) {
            const udmThenMatch = remainderPart.match(UDM_THEN_REGEX);
            if (udmThenMatch) {
                [, udmFee, ongoingFee] = udmThenMatch;
            } else {
                const udmOnlyMatch = remainderPart.match(UDM_ONLY_REGEX);
                if (udmOnlyMatch) {
                    [, udmFee] = udmOnlyMatch;
                } else {
                    const numMatch = remainderPart.match(NUMBER_REGEX);
                    if (numMatch) {
                        [, ongoingFee] = numMatch;
                    }
                }
            }
        }

        const hasFee = Boolean(evalFee || udmFee || ongoingFee);
        if (hasFee) {
            return { evalFee, evalSessions, udmFee, ongoingFee, isTbd };
        }
        return null;
    } catch (err) {
        Sentry.captureException(err);
    }
    return null;
}

export const getMessageForFees = (inputFee = "") => {
    const fees = calculateFees(inputFee);
    let message = "";
    if (fees) {
        const hasEval = fees.evalFee && fees.evalSessions;
        const prefix = hasEval ? "then" : "Client pays";
        if (hasEval) {
            const plural = Number(fees.evalSessions) === 1 ? "" : "s";
            message +=
                `Client pays $${fees.evalFee}/session` +
                ` for the first ${fees.evalSessions} session${plural} `;
        }

        if (fees.udmFee) {
            message += `${prefix} $${fees.udmFee}/session until deductible is met `;
            if (fees.ongoingFee) {
                message += `then $${fees.ongoingFee}/session `;
            }
        } else if (fees.ongoingFee) {
            if (fees.isTbd) {
                message +=
                    `${prefix} $${fees.ongoingFee}/session` +
                    ` until their insurance company responds to our first claim `;
            } else {
                message += `${prefix} $${fees.ongoingFee}/session `;
            }
        }
    }

    return message;
};
